import type { Names, Round, Seat, AutoGeneratedMade, AppState } from '../../types';
import { scoringFieldPath, teamStorageKey } from './scoringFields';

/**
 * Viewer-perspective rotation.
 *
 * The scorecard renders a fixed set of four player slots. Around the table
 * (counterclockwise) they are: t1p1 (bottom/"you"), t2p1 (left), t1p2
 * (top/partner), t2p2 (right). To show the board from another player's
 * perspective we rotate the four seats so the chosen player lands in the
 * "you" slot — NOT mirror the two teams (a mirror inverts left/right, which
 * is the bug this replaces).
 */

/** Fixed table order, counterclockwise from the leader's seat. */
export const SEAT_ORDER: Seat[] = ['t1p1', 't2p1', 't1p2', 't2p2'];

export const SEAT_INDEX: Record<Seat, number> = {
  t1p1: 0,
  t2p1: 1,
  t1p2: 2,
  t2p2: 3,
};

/** True when the seat belongs to Team 2 (the team shown in the right column). */
export function isOppositeTeam(seat: Seat | null | undefined): boolean {
  return seat === 't2p1' || seat === 't2p2';
}

const NAME_KEY: Record<Seat, keyof Names> = {
  t1p1: 't1p1Name',
  t2p1: 't2p1Name',
  t1p2: 't1p2Name',
  t2p2: 't2p2Name',
};

/** Maps each seat to its (team, player) fields inside a Round. */
const ROUND_SLOT: Record<
  Seat,
  { team: 'team1BidsAndMade' | 'team2BidsAndMade'; player: 'p1' | 'p2' }
> = {
  t1p1: { team: teamStorageKey(1), player: 'p1' },
  t2p1: { team: teamStorageKey(2), player: 'p1' },
  t1p2: { team: teamStorageKey(1), player: 'p2' },
  t2p2: { team: teamStorageKey(2), player: 'p2' },
};

/** Maps each seat to the dealer-id string used by firstDealerOrder / dealerOverride. */
const DEALER_ID_BY_SEAT: Record<Seat, string> = {
  t1p1: scoringFieldPath(1, 'p1', 'Bid'),
  t2p1: scoringFieldPath(2, 'p1', 'Bid'),
  t1p2: scoringFieldPath(1, 'p2', 'Bid'),
  t2p2: scoringFieldPath(2, 'p2', 'Bid'),
};

const SEAT_BY_DEALER_ID: Record<string, Seat> = {
  [scoringFieldPath(1, 'p1', 'Bid')]: 't1p1',
  [scoringFieldPath(2, 'p1', 'Bid')]: 't2p1',
  [scoringFieldPath(1, 'p2', 'Bid')]: 't1p2',
  [scoringFieldPath(2, 'p2', 'Bid')]: 't2p2',
};

/** Maps each seat to its auto-generated made flag key inside a Round. */
const AUTO_GEN_KEY_BY_SEAT: Record<Seat, keyof AutoGeneratedMade> = {
  t1p1: 'team1P1',
  t2p1: 'team2P1',
  t1p2: 'team1P2',
  t2p2: 'team2P2',
};

/** Rotate the four player names so `seat` is in the "you" (t1p1) slot. */
export function rotateNames(names: Names, seat: Seat): Names {
  const p = SEAT_INDEX[seat];
  if (p === 0) return names;

  const result: Names = { ...names };
  for (const posSeat of SEAT_ORDER) {
    const k = SEAT_INDEX[posSeat];
    const srcSeat = SEAT_ORDER[(k + p) % 4];
    result[NAME_KEY[posSeat]] = names[NAME_KEY[srcSeat]];
  }

  // The viewer's own team goes in the left column. Team 2 viewers see the
  // team labels swapped; Team 1 viewers keep them.
  if (isOppositeTeam(seat)) {
    result.team1Name = names.team2Name;
    result.team2Name = names.team1Name;
  }
  return result;
}

/** Rotate a round's bids/made the same way as the names. */
export function rotateRound(round: Round, seat: Seat): Round {
  const p = SEAT_INDEX[seat];
  if (p === 0) return round;

  const result: Round = { ...round };
  for (const posSeat of SEAT_ORDER) {
    const k = SEAT_INDEX[posSeat];
    const srcSeat = SEAT_ORDER[(k + p) % 4];
    const src = ROUND_SLOT[srcSeat];
    const dst = ROUND_SLOT[posSeat];

    result[dst.team] = {
      ...result[dst.team],
      [`${dst.player}Bid`]: round[src.team][`${src.player}Bid`],
      [`${dst.player}Made`]: round[src.team][`${src.player}Made`],
    };
  }

  // The auto-generated made flag is per-player, so it must rotate with the
  // player too — otherwise the '*' marker lands on the wrong name.
  if (result.autoGeneratedMade) {
    const rotatedAuto = { ...result.autoGeneratedMade } as AutoGeneratedMade;
    for (const posSeat of SEAT_ORDER) {
      const k = SEAT_INDEX[posSeat];
      const srcSeat = SEAT_ORDER[(k + p) % 4];
      rotatedAuto[AUTO_GEN_KEY_BY_SEAT[posSeat]] =
        round.autoGeneratedMade?.[AUTO_GEN_KEY_BY_SEAT[srcSeat]] ?? false;
    }
    result.autoGeneratedMade = rotatedAuto;
  }

  return result;
}

/**
 * Rotates a dealer-id string (as stored in firstDealerOrder / dealerOverride)
 * into a viewer's perspective. The dealer is a fact about a *player*, so after
 * the board is rotated the badge must land on that same player's new slot —
 * not stay glued to the fixed slot it occupied in the leader's view.
 */
export function rotateDealerId(dealerId: string, seat: Seat): string {
  const p = SEAT_INDEX[seat];
  if (p === 0) return dealerId;
  const srcSeat = SEAT_BY_DEALER_ID[dealerId];
  if (!srcSeat) return dealerId;
  const s = SEAT_INDEX[srcSeat];
  const k = (s - p + 4) % 4;
  return DEALER_ID_BY_SEAT[SEAT_ORDER[k]];
}

/**
 * Rotate a round's bids/made AND its dealer override to the viewer's seat.
 * `rotateRound` leaves `dealerOverride` alone (it is handled at display time by
 * `DealerTag`), but when the round becomes the seed of a NEW local game the
 * dealer must be baked in rotated so the badge lands on the right player.
 */
export function rotateRoundWithDealer(round: Round, seat: Seat): Round {
  const rotated = rotateRound(round, seat);
  if (round.dealerOverride != null) {
    rotated.dealerOverride = rotateDealerId(round.dealerOverride, seat);
  }
  return rotated;
}

/**
 * Rotate an ENTIRE game state into a viewer's perspective. Used when a viewer
 * leaves and their watched game is seeded as their own local game, so every
 * per-player fact (names, bids/made, auto-generated markers, dealer order,
 * dealer override) is consistent with the viewer now sitting in the "you" seat.
 * Game-level fields (`isFirstGameAmongTeammates`, `nilScoringRule`) pass through
 * verbatim.
 */
export function rotateGameState(state: AppState, seat: Seat): AppState {
  const p = SEAT_INDEX[seat];
  if (p === 0) return state;
  return {
    ...state,
    names: rotateNames(state.names, seat),
    currentRound: rotateRoundWithDealer(state.currentRound, seat),
    roundHistory: state.roundHistory.map((round) =>
      rotateRoundWithDealer(round, seat),
    ),
    firstDealerOrder: state.firstDealerOrder.map((dealerId) =>
      rotateDealerId(dealerId, seat),
    ),
  };
}
